import React from "react";
import { Modal, t } from "./CustomComponent.js";
import { formatNumber } from "../utils/formatting";

/**
 * หน้าตรวจข้อมูลก่อนนำเข้า — ใช้ร่วมกันทุกที่ที่มีการนำเข้าไฟล์
 * (Import Center และปุ่มนำเข้าในเมนูสต็อกสินค้า) เพื่อให้ "วิธีการเช็ค" และสิ่งที่ผู้ใช้เห็น
 * เหมือนกันทุกหน้า ต่างกันแค่จุดที่กดเข้ามา
 *
 * ตัวคอมโพเนนต์ไม่ตรวจข้อมูลเอง — ผู้เรียกส่ง rows ที่ตรวจแล้วเข้ามา
 * rows = [{ row_no?, valid, cells: { [key]: { raw, value, error, detail? } } }]
 */

/** error code จากตัวตรวจไฟล์ -> ข้อความที่ผู้ใช้อ่านรู้เรื่อง */
export const importErrorText = (code, detail = "") => {
  switch (code) {
    case "required": return t("import_err_required");
    case "number": return t("import_err_number");
    case "email": return t("import_err_email");
    case "date": return t("import_err_date");
    case "enum": return t("import_err_enum");
    case "status": return t("import_err_status");
    case "customer_status": return t("import_err_customer_status");
    case "bool": return t("import_err_bool");
    case "duplicate": return t("import_err_duplicate");
    case "lookup": return t("import_err_lookup");
    // มีรหัสนี้ใน master จริง แต่ไปผูกกับคลังอื่น (detail = รหัสคลังที่แถวนั้นระบุมา)
    case "location_plant": return t("import_err_location_plant");
    case "parent_not_found": return t("import_err_parent");
    // detail = จำนวนตัวอักษรสูงสุดที่คอลัมน์นั้นรับได้
    case "max_length": return t("import_err_max_length");
    case "sap_unit": return t("import_err_sap_unit");
    // detail = ชื่อสาขาที่กรอกมา
    case "branch_not_found": return t("import_err_branch_not_found").replace("{name}", detail);
    case "branch_inactive": return t("import_err_branch_inactive").replace("{name}", detail);
    default: return "";
  }
};

/**
 * หมายเหตุระดับช่อง — ไม่ใช่ error แค่บอกว่านำเข้าแล้วจะเกิดอะไรขึ้น
 * (เช่น รหัสวัสดุ / คลัง / พื้นที่เก็บ ที่ยังไม่มีใน master ระบบจะสร้างให้)
 */
export const importNoteText = (code) => {
  switch (code) {
    case "will_create": return t("import_note_will_create");
    case "will_create_warehouse": return t("import_note_will_create_warehouse");
    case "will_create_location": return t("import_note_will_create_location");
    case "will_create_uom": return t("import_note_will_create_uom");
    case "will_create_material_type": return t("import_note_will_create_material_type");
    case "will_create_material_group": return t("import_note_will_create_material_group");
    case "will_create_brand": return t("import_note_will_create_brand");
    default: return "";
  }
};

/**
 * สรุปผลการนำเข้าสต็อก — ใช้ข้อความชุดเดียวกันทั้งเมนูสต็อกสินค้าและ Import Center
 * "ประวัติเคลื่อนไหวที่ลบ" แสดงเฉพาะตอนมีจริง เพราะเป็นข้อมูลที่หายถาวร ผู้ใช้ควรเห็นตัวเลข
 */
export const stockImportSummaryText = (summary = {}) => {
  const parts = [
    `${t("import_stock_rows")}: ${formatNumber(summary.stocks_created ?? 0)}`,
    `${t("import_stock_lots")}: ${formatNumber(summary.lots_created ?? 0)}`,
    `${t("import_stock_removed")}: ${formatNumber(summary.stocks_deleted ?? 0)}`,
  ];
  // อะไหล่ / คลัง / พื้นที่เก็บ ที่ระบบสร้างให้เป็นข้อมูล master ที่เพิ่งเกิดใหม่ ผู้ใช้ควรเห็นตัวเลขเพื่อไปตรวจต่อ
  if (summary.parts_created) parts.push(`${t("import_stock_parts_created")}: ${formatNumber(summary.parts_created)}`);
  if (summary.warehouses_created) parts.push(`${t("import_stock_warehouses_created")}: ${formatNumber(summary.warehouses_created)}`);
  if (summary.locations_created) parts.push(`${t("import_stock_locations_created")}: ${formatNumber(summary.locations_created)}`);
  if (summary.movements_deleted) parts.push(`${t("import_stock_history_removed")}: ${formatNumber(summary.movements_deleted)}`);
  return parts.join("  |  ");
};

const ImportPreviewModal = ({
  show,
  title,
  subtitle = "",
  warning = "", // ข้อความเตือนบนสุด (เช่น การนำเข้านี้จะลบสต็อกเดิมทั้งหมด)
  columns = [],
  rows = [],
  importing = false,
  progress = 0,
  done = 0,
  showInvalidOnly = false,
  onToggleInvalidOnly,
  // ตัวเลือกที่ต้องเลือกก่อนยืนยัน แล้วใช้กับทุกแถวในไฟล์ (เช่น บทบาทของช่าง)
  // ไม่ส่ง bulkOption มา = โมดัลทำงานเหมือนเดิมทุกอย่าง
  bulkOption = null, // { label, placeholder, hint, valueField, labelField } (bulkOptions มาจาก endpoint หรือรายการคงที่ก็ได้)
  bulkOptions = [],
  bulkValue = "",
  onBulkChange,
  onCancel,
  onConfirm,
}) => {
  // คอลัมน์ auto เป็นเลขลำดับที่ระบบออกให้เอง ไม่ต้องให้ผู้ใช้ตรวจ
  const previewColumns = columns.filter((c) => c.type !== "auto");

  const invalidCount = rows.filter((r) => !r.valid).length;
  const validCount = rows.length - invalidCount;
  // ชุดข้อมูลที่ประกาศ bulkOption ต้องเลือกค่าก่อน ถึงจะยืนยันนำเข้าได้
  const bulkMissing = !!bulkOption && !bulkValue;
  // ตัวเลือกที่ปิดใช้งานไม่ควรเลือกใหม่ได้ แต่ค่าที่เลือกไว้แล้วต้องยังแสดงอยู่
  const bulkChoices = (bulkOptions || []).filter(
    (o) => o?.is_active !== false || (bulkOption && String(o?.[bulkOption.valueField]) === String(bulkValue))
  );
  const canConfirm = rows.length > 0 && invalidCount === 0 && !bulkMissing && !importing;

  // ติดเลขไว้ก่อนกรอง เพื่อให้ตอนดูเฉพาะแถวผิดยังรู้ว่าเป็นแถวที่เท่าไรของไฟล์
  //   no      = ลำดับข้อมูล เริ่มที่ 1 (คอลัมน์ #)
  //   fileRow = เลขแถวจริงในไฟล์ Excel ซึ่งไม่ใช่ 1 เพราะแถวแรก ๆ เป็นหัวตาราง
  //             เก็บไว้โชว์ใน tooltip เพราะข้อความแจ้ง error อ้างเลขนี้ ต้องเปิดไฟล์ตามได้
  const previewRows = rows
    .map((row, index) => ({ row, no: index + 1, fileRow: row.row_no }))
    .filter((r) => !showInvalidOnly || !r.row.valid);

  return (
    <Modal
      size="xl"
      show={show}
      onHide={importing ? undefined : onCancel}
      centered
      contentClassName="border-0 rounded-4 overflow-hidden shadow-lg"
    >
      <Modal.Header className="border-bottom border-light px-4 py-3 bg-white" closeButton={!importing}>
        <div>
          <h5 className="fw-bold mb-0 text-dark">{title}</h5>
          <div className="text-secondary text-12">{subtitle}</div>
        </div>
      </Modal.Header>

      <Modal.Body className="p-4 bg-white">
        {/* คำเตือนของชุดข้อมูลที่นำเข้าแล้วข้อมูลเดิมหาย ต้องเห็นตั้งแต่ก่อนกดยืนยัน */}
        {warning ? (
          <div className="alert alert-warning border-0 rounded-3 text-13 py-2 px-3 mb-3">{warning}</div>
        ) : null}

        {/* ความคืบหน้า — modal ปิดไม่ได้และปุ่มถูกล็อกระหว่างนำเข้า */}
        {importing && (
          <div className="bg-primary-light rounded-3 p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold text-primary text-13">
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                {t("importing")}
              </span>
              <span className="fw-bold text-primary text-15">{progress}%</span>
            </div>
            <div className="progress" style={{ height: "10px" }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                role="progressbar"
                style={{ width: `${progress}%`, transition: "width 0.3s ease" }}
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="text-primary text-11 fw-bold mt-2">
              {formatNumber(done)} / {formatNumber(rows.length)} {t("rows")}
            </div>
          </div>
        )}

        {/* ตัวเลือกที่ใช้กับทุกแถวในไฟล์ (ชุดข้อมูลช่าง = บทบาทของบัญชีผู้ใช้) */}
        {bulkOption && (
          <div className={`rounded-3 p-3 mb-3 border ${bulkMissing ? "border-danger bg-danger-light" : "border-light bg-light"}`}>
            <div className="d-flex flex-wrap align-items-center gap-3">
              <label className="fw-bold text-dark text-14 mb-0">
                {t(bulkOption.label)} <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${bulkMissing ? "border-danger" : ""}`}
                style={{ height: "38px", maxWidth: "320px" }}
                value={bulkValue}
                disabled={importing}
                onChange={(e) => onBulkChange && onBulkChange(e.target.value)}
              >
                <option value="">{t(bulkOption.placeholder)}</option>
                {bulkChoices.map((opt) => (
                  <option key={opt[bulkOption.valueField]} value={opt[bulkOption.valueField]}>
                    {opt[bulkOption.labelField]}
                  </option>
                ))}
              </select>
              <span className={`text-12 ${bulkMissing ? "text-danger fw-bold" : "text-secondary"}`}>
                {t(bulkOption.hint)}
              </span>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-center">
          <span className="badge bg-light text-dark border rounded-pill px-3 py-2 fw-bold text-12">
            {t("total_rows")}: {formatNumber(rows.length)}
          </span>
          <span className="badge bg-success-light text-success rounded-pill px-3 py-2 fw-bold text-12">
            {t("rows_valid")}: {formatNumber(validCount)}
          </span>
          <span
            className={`badge rounded-pill px-3 py-2 fw-bold text-12 ${invalidCount > 0 ? "pointer" : ""} ${
              showInvalidOnly ? "bg-danger text-white" : "bg-danger-light text-danger"
            }`}
            onClick={() => invalidCount > 0 && onToggleInvalidOnly && onToggleInvalidOnly(!showInvalidOnly)}
            title={invalidCount > 0 ? t("show_invalid_only") : ""}
          >
            {t("rows_invalid")}: {formatNumber(invalidCount)}
            {invalidCount > 0 && <span className="ms-2">{showInvalidOnly ? "✕" : "▼"}</span>}
          </span>
          {showInvalidOnly && (
            <span
              className="badge bg-light text-dark border rounded-pill px-3 py-2 fw-bold text-12 pointer"
              onClick={() => onToggleInvalidOnly && onToggleInvalidOnly(false)}
            >
              {t("show_all_rows")}
            </span>
          )}
          <span className="text-secondary text-11 d-flex align-items-center">
            <span className="d-inline-block wpx-12 hpx-12 rounded bg-danger-light border border-danger me-2" />
            {t("legend_invalid")}
          </span>
        </div>

        {/* Preview Table */}
        <div className="table-responsive border rounded-3" style={{ maxHeight: "420px", overflow: "auto" }}>
          <table className="table table-bordered align-middle mb-0 text-13" style={{ minWidth: "900px" }}>
            <thead className="position-sticky top-0" style={{ zIndex: 1 }}>
              <tr className="bg-light text-dark fw-bold text-12">
                <th className="py-2 text-center bg-light" style={{ width: "60px" }}>#</th>
                {previewColumns.map((c) => (
                  // คุมหัวคอลัมน์ไว้ 2 บรรทัด (ไทย + อังกฤษ) ไม่ให้ตัวยาวดันหัวตารางสูงจนบังข้อมูล
                  // ไม่ตัดข้อความ ปล่อยให้คอลัมน์กว้างตามเนื้อหา แล้วเลื่อนตารางแนวนอนเอา
                  <th key={c.key} className="py-2 bg-light text-nowrap">
                    <div>{c.th}</div>
                    <div className="text-secondary text-10 fw-normal">{c.en}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.length === 0 ? (
                <tr>
                  <td colSpan={previewColumns.length + 1} className="text-center text-secondary py-4">
                    {t("no_data")}
                  </td>
                </tr>
              ) : (
                previewRows.map(({ row, no, fileRow }) => (
                  <tr key={no} className={row.valid ? "" : "bg-danger-subtle"}>
                    <td className="text-center text-secondary" title={fileRow ? `${t("row")} ${fileRow}` : undefined}>
                      {no}
                    </td>
                    {previewColumns.map((c) => {
                      const cell = row.cells[c.key] || { raw: "", error: "" };
                      const hasError = !!cell.error;
                      // หมายเหตุจะโชว์เฉพาะตอนไม่มี error เพื่อไม่ให้แย่งความสนใจจากสิ่งที่ต้องแก้ก่อน
                      const noteText = !hasError && cell.note ? importNoteText(cell.note) : "";
                      // enrichRows เติมค่าที่ผู้ใช้ควรเห็นไว้ใน display (เช่น รหัสผ่านที่ถูกปิดบัง)
                      const shown = cell.display != null ? cell.display : cell.raw;
                      const shownNode = shown !== "" ? shown : <span className="text-secondary">-</span>;
                      // ช่องที่ผิดสูงบรรทัดเดียว ค่าแสดงเต็มไม่ตัด เหตุผลอยู่ใน tooltip
                      // (ช่องแดงอยู่แล้ว เห็นได้ว่าต้องแก้ตรงไหนโดยไม่ต้องชี้)
                      // ส่วนหมายเหตุ "ระบบจะสร้างให้ใหม่" ไม่ใช่สิ่งที่ต้องแก้ แต่เป็นผลที่จะเกิดตามมา
                      // ถ้าไม่เห็นตั้งแต่แรกผู้ใช้จะไม่รู้ว่ากดยืนยันแล้วจะมีข้อมูลใหม่โผล่ใน master จึงให้ 2 บรรทัดได้
                      const reason = hasError
                        ? importErrorText(cell.error, cell.detail) +
                          (cell.detail && !cell.error.startsWith("branch_") ? `: ${cell.detail}` : "")
                        : noteText;
                      return (
                        <td
                          key={c.key}
                          title={reason || undefined}
                          className={`text-nowrap ${
                            hasError ? "bg-danger-light text-danger fw-bold" : noteText ? "bg-warning-light text-dark" : "text-dark"
                          }`}
                        >
                          {noteText ? (
                            <React.Fragment>
                              <div>{shownNode}</div>
                              <div className="text-warning text-10 fw-bold">{noteText}</div>
                            </React.Fragment>
                          ) : (
                            shownNode
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top border-light px-4 py-3 bg-white d-flex justify-content-between align-items-center">
        <div className="text-secondary text-12">
          {importing
            ? t("importing")
            : invalidCount > 0
            ? t("fix_errors_first")
            : bulkMissing
            ? t("import_select_bulk_first").replace("{field}", t(bulkOption.label))
            : t("ready_to_import_hint")}
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-white border px-4 py-2 text-dark rounded-3 fw-bold"
            disabled={importing}
            onClick={onCancel}
          >
            {t("cancel")}
          </button>
          <button
            className="btn btn-primary px-4 py-2 d-flex align-items-center rounded-3 fw-bold text-white shadow-sm"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {importing && <span className="spinner-border spinner-border-sm me-2" role="status" />}
            {importing ? (
              `${progress}%`
            ) : (
              <>
                <span className="icon me-2">{""}</span> {t("confirm_import")}
              </>
            )}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportPreviewModal;
